import { useCallback } from 'react';
import useCustomNavigate from 'hooks/useCustomeNavigate';
import { useQueryClient } from 'react-query';
import { AccountCacheKeys } from 'types/cacheKeys.types';
import type { AccountantAuthenticationDataType } from 'types/auth.types';
import { getStoredWorkspaceId } from 'utils/workspaceStorage';
import { EXPERT_ROUTES } from 'applications/expert/routing/expertRoutes';
import useWorkspaceDetails from 'hooks/useWorkspaceDetails';

export interface UseExpertSigninSuccessOptions {
  /** If true, redirects to set-password when accountant has no auth methods (e.g. first magic link login). Default false. */
  redirectToSetPasswordIfNoAuthMethods?: boolean;
}

/**
 * Shared logic for expert sign-in success: store auth, sync user email, set query data,
 * and navigate based on workspace ID. Use in ExpertLogin, ExpertTOTP, ExpertRecoveryCode,
 * useMagicLinkHandler, and ExpertSetPassword (checkWorkspaceAccess only).
 */
export const useExpertSigninSuccess = (
  options: UseExpertSigninSuccessOptions = {},
) => {
  const navigate = useCustomNavigate();
  const queryClient = useQueryClient();
  const { redirectToSetPasswordIfNoAuthMethods = false } = options;

  const checkWorkspaceAccess = useCallback(() => {
    const existingWorkspaceId = getStoredWorkspaceId();
    if (existingWorkspaceId) {
      navigate('/', { replace: true, asDefault: true });
      return;
    }
    navigate(`${EXPERT_ROUTES.WORKSPACE}?autoSelect=true`, {
      replace: true,
      asDefault: true,
    });
  }, [navigate]);

  const onSigninSuccess = useCallback(
    (result: AccountantAuthenticationDataType): void => {
      localStorage.setItem('auth', JSON.stringify(result));
      useWorkspaceDetails.getState().syncUserEmail();
      if (result.accountant) {
        queryClient.setQueryData(
          [AccountCacheKeys.AccountantData],
          result.accountant,
        );
        if (redirectToSetPasswordIfNoAuthMethods) {
          const authMethods = result.accountant.auth_methods || [];
          if (authMethods.length === 0) {
            navigate(EXPERT_ROUTES.SET_PASSWORD, {
              replace: true,
              asDefault: true,
            });
            return;
          }
        }
      }
      checkWorkspaceAccess();
    },
    [
      checkWorkspaceAccess,
      navigate,
      queryClient,
      redirectToSetPasswordIfNoAuthMethods,
    ],
  );

  return { onSigninSuccess, checkWorkspaceAccess };
};
